import React, { useState } from "react";

import { Button } from "src/design-system/Button";
import { Modal } from "src/design-system/Modal";
import { Tooltip } from "src/design-system/Tooltip";
import { CredentialField } from "src/oauthClients/CredentialField";
import { PrivateKeyHandoff } from "src/oauthClients/PrivateKeyHandoff";

export type OAuthClientCredentials = {
  clientName: string;
  clientId: string;
  kid: string;
  tokenEndpoint: string;
  /** Present only when the key pair was generated in the browser. */
  privateKeyPem?: string;
};

type Props = {
  credentials?: OAuthClientCredentials;
  open: boolean;
  onClose: () => void;
  afterLeave: () => void;
};

export const ShowOAuthClientCredentialsModal: React.FC<Props> = ({
  credentials,
  open,
  onClose,
  afterLeave,
}) => {
  const [hasTakenKey, setHasTakenKey] = useState(false);
  const [isSaveConfirmed, setIsSaveConfirmed] = useState(false);

  const privateKeyPem = credentials?.privateKeyPem;
  // The generated private key exists nowhere else, so the dialog stays shut
  // until the customer confirms they have the file.
  const isCloseBlocked = Boolean(privateKeyPem) && !isSaveConfirmed;

  const handleAfterLeave = () => {
    setHasTakenKey(false);
    setIsSaveConfirmed(false);
    afterLeave();
  };

  return (
    <Modal
      afterLeave={handleAfterLeave}
      closeMode={isCloseBlocked ? "persistent" : "standard"}
      open={open}
      onClose={onClose}
    >
      <Modal.Header hideCloseIcon={isCloseBlocked}>
        Your new OAuth client
      </Modal.Header>
      <Modal.Content>
        <div className="flex flex-col gap-4">
          {privateKeyPem && (
            <PrivateKeyHandoff
              clientName={credentials?.clientName ?? ""}
              dataLocPrefix="oauth-client"
              hasTakenKey={hasTakenKey}
              isSaveConfirmed={isSaveConfirmed}
              privateKeyPem={privateKeyPem}
              onKeyTaken={() => setHasTakenKey(true)}
              onSaveConfirmedChange={setIsSaveConfirmed}
            />
          )}
          <CredentialField
            dataLoc="oauth-client-id"
            label="Client ID"
            value={credentials?.clientId ?? ""}
          />
          <CredentialField
            dataLoc="oauth-client-kid"
            label="Key ID (kid)"
            value={credentials?.kid ?? ""}
          />
          <CredentialField
            dataLoc="oauth-client-token-endpoint"
            label="Token endpoint"
            value={credentials?.tokenEndpoint ?? ""}
          />
        </div>
      </Modal.Content>
      <Modal.Footer
        primaryButton={
          <Tooltip
            disabled={!isCloseBlocked}
            placement="top"
            title="Confirm you have saved the private key before closing"
            asChild
          >
            <Button
              dataLoc="oauth-client-credentials-done"
              isDisabled={isCloseBlocked}
              variant="primary"
              onClick={onClose}
            >
              Done
            </Button>
          </Tooltip>
        }
        secondaryButton={false}
      />
    </Modal>
  );
};
